import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheckIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  EyeIcon,
  Cog6ToothIcon,
  UsersIcon,
  KeyIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import {
  ShieldCheckIcon as ShieldCheckIconSolid,
  LockClosedIcon as LockClosedIconSolid
} from '@heroicons/react/24/solid';

import axiosInstance from '../api/axiosInstance';
import BreadcrumbNavigation from './BreadcrumbNavigation';

// Design System Components
import Button from '../ui/Button';
import InputComponent, { SearchInput, Textarea } from '../ui/Input';
import Card, { CardHeader } from '../ui/Card';
import Table, { StatusBadge, ActionMenu } from '../ui/Table';
import SidePanel from '../ui/SidePanel';
import logger from '../../utils/logger';

// RoleForm Component - Moved outside to prevent re-creation on each render
const RoleForm = ({ isEdit = false, newRole, setNewRole, formErrors, setFormErrors, availablePermissions, handlePermissionChange, formatPermissionName }) => (
  <div className="space-y-4">
    {/* Basic Information */}
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
      <div className="flex items-center space-x-2 mb-3">
        <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
          <ClipboardDocumentListIcon className="w-4 h-4 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-sm text-gray-900 dark:text-white">Basic Information</h3>
          <p className="text-xs text-gray-600 dark:text-gray-300">Define the role's identity</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <InputComponent
          label="Role Name"
          placeholder="e.g., marketing_manager"
          value={newRole.name}
          onChange={(e) => {
            setNewRole(prev => ({ ...prev, name: e.target.value.toLowerCase().replace(/\s+/g, '_') }));
            if (formErrors.name) setFormErrors(prev => ({ ...prev, name: null }));
          }}
          error={formErrors.name}
          required
          disabled={isEdit}
        />

        <InputComponent
          label="Display Name"
          placeholder="e.g., Marketing Manager"
          value={newRole.displayName}
          onChange={(e) => {
            setNewRole(prev => ({ ...prev, displayName: e.target.value }));
            if (formErrors.displayName) setFormErrors(prev => ({ ...prev, displayName: null }));
          }}
          error={formErrors.displayName}
          required
        />
      </div>

      <div className="mt-3">
        <Textarea
          label="Description"
          placeholder="Describe the role's purpose and responsibilities"
          value={newRole.description}
          onChange={(e) => {
            setNewRole(prev => ({ ...prev, description: e.target.value }));
            if (formErrors.description) setFormErrors(prev => ({ ...prev, description: null }));
          }}
          error={formErrors.description}
          rows={2}
          required
        />
      </div>

      <div className="mt-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          Permission Level
        </label>
        <select
          value={newRole.level}
          onChange={(e) => {
            setNewRole(prev => ({ ...prev, level: parseInt(e.target.value) }));
            if (formErrors.level) setFormErrors(prev => ({ ...prev, level: null }));
          }}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
        >
          <option value={20}>Level 20 - Basic User</option>
          <option value={40}>Level 40 - Senior User</option>
          <option value={50}>Level 50 - Team Lead</option>
          <option value={60}>Level 60 - Manager</option>
          <option value={70}>Level 70 - Senior Manager</option>
          <option value={80}>Level 80 - Admin</option>
        </select>
        {formErrors.level && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{formErrors.level}</p>
        )}
      </div>
    </div>

    {/* Permissions */}
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
      <div className="flex items-center space-x-2 mb-3">
        <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
          <KeyIcon className="w-4 h-4 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-sm text-gray-900 dark:text-white">Permissions</h3>
          <p className="text-xs text-gray-600 dark:text-gray-300">Select capabilities for this role</p>
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto space-y-3">
        {Object.entries(availablePermissions).map(([category, permissions]) => (
          <div key={category} className="bg-white dark:bg-[#222222] rounded-lg p-3 border border-gray-200 dark:border-gray-800">
            <h4 className="font-medium text-sm text-gray-900 dark:text-white mb-2 capitalize flex items-center">
              <Cog6ToothIcon className="w-4 h-4 mr-2 text-gray-500" />
              {category.replace(/_/g, ' ')}
            </h4>
            <div className="grid md:grid-cols-2 gap-2">
              {permissions.map((permission) => {
                const permissionName = typeof permission === 'string' ? permission : permission?.name || permission;
                const isChecked = newRole.permissions.some(p => {
                  const pName = typeof p === 'string' ? p : p?.name || p;
                  return pName === permissionName;
                });

                return (
                  <label key={permissionName} className="flex items-start space-x-2 p-2 rounded hover:bg-gray-50 dark:hover:bg-[#2a2a2a] cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => handlePermissionChange(permissionName, e.target.checked)}
                      className="w-4 h-4 mt-0.5 text-orange-600 border-gray-300 dark:border-gray-700 rounded focus:ring-orange-500 bg-white dark:bg-[#2a2a2a]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900 dark:text-white">
                        {formatPermissionName(permissionName)}
                      </div>
                      {permission.description && (
                        <div className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 line-clamp-1">
                          {permission.description}
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ModernRoleManagement = ({ showNotification }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [showPermissionsPanel, setShowPermissionsPanel] = useState(false);
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [newRole, setNewRole] = useState({
    name: '',
    displayName: '',
    description: '',
    level: 20,
    permissions: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/business/roles');
      
      if (response.data.success) {
        setRoles(response.data.roles || []);

      }
    } catch (error) {
      logger.error('❌ Error fetching roles:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load roles';
      showNotification && showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  const fetchAvailablePermissions = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/api/business/roles/permissions/list');
      
      if (response.data.success) {
        setAvailablePermissions(response.data.permissions || {});
      }
    } catch (error) {
      logger.error('Error fetching permissions:', error);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
    fetchAvailablePermissions();
  }, [fetchRoles, fetchAvailablePermissions]);

  const validateForm = () => {
    const errors = {};
    
    if (!newRole.name.trim()) {
      errors.name = 'Role name is required';
    } else if (!/^[a-z_]+$/.test(newRole.name)) {
      errors.name = 'Role name must be lowercase letters and underscores only';
    }
    
    if (!newRole.displayName.trim()) {
      errors.displayName = 'Display name is required';
    }
    
    if (!newRole.description.trim()) {
      errors.description = 'Description is required';
    } else if (newRole.description.length < 10) {
      errors.description = 'Description must be at least 10 characters';
    }
    
    if (newRole.level < 1 || newRole.level > 99) {
      errors.level = 'Level must be between 1 and 99';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateRole = async () => {
    if (!validateForm()) {
      showNotification && showNotification('Please fix form errors', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axiosInstance.post('/api/business/roles', newRole);
      
      if (response.data.success) {
        showNotification && showNotification('Role created successfully', 'success');
        setShowCreatePanel(false);
        resetForm();
        fetchRoles();
      }
    } catch (error) {
      logger.error('❌ Error creating role:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create role';
      showNotification && showNotification(errorMessage, 'error');
      
      if (error.response?.status === 400 && error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditRole = async () => {
    if (!validateForm()) {
      showNotification && showNotification('Please fix form errors', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axiosInstance.put(`/api/business/roles/${selectedRole._id}`, newRole);
      
      if (response.data.success) {
        showNotification && showNotification('Role updated successfully', 'success');
        setShowEditPanel(false);
        resetForm();
        fetchRoles();
      }
    } catch (error) {
      logger.error('❌ Error updating role:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update role';
      showNotification && showNotification(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) {
      return;
    }

    try {
      const response = await axiosInstance.delete(`/api/business/roles/${roleId}`);
      
      if (response.data.success) {
        showNotification && showNotification('Role deleted successfully', 'success');
        fetchRoles();
      }
    } catch (error) {
      logger.error('Error deleting role:', error);
      showNotification && showNotification(
        error.response?.data?.message || 'Failed to delete role', 
        'error'
      );
    }
  };

  const initializeRoles = async () => {
    try {
      const response = await axiosInstance.post('/api/business/roles/initialize');
      
      if (response.data.success) {
        showNotification && showNotification('Default roles initialized successfully', 'success');
        fetchRoles();
      }
    } catch (error) {
      logger.error('Error initializing roles:', error);
      showNotification && showNotification('Failed to initialize roles', 'error');
    }
  };

  const resetForm = () => {
    setNewRole({
      name: '',
      displayName: '',
      description: '',
      level: 20,
      permissions: []
    });
    setFormErrors({});
    setSelectedRole(null);
  };

  const handleEditClick = (role) => {

    setSelectedRole(role);

    // Normalize permissions to always be strings
    const normalizedPermissions = (role.permissions || []).map(p =>
      typeof p === 'string' ? p : p?.name || p
    );

    setNewRole({
      name: role.name,
      displayName: role.displayName,
      description: role.description,
      level: role.level,
      permissions: normalizedPermissions
    });
    setShowEditPanel(true);
  };

  const getRoleLevelColor = (level) => {
    if (level >= 100) return 'error';
    if (level >= 80) return 'warning';
    if (level >= 70) return 'primary';
    if (level >= 60) return 'secondary';
    if (level >= 40) return 'success';
    return 'gray';
  };

  const getRoleLevelText = (level) => {
    if (level >= 100) return 'System Admin';
    if (level >= 80) return 'Senior Admin';
    if (level >= 70) return 'Admin';
    if (level >= 60) return 'Manager';
    if (level >= 40) return 'Senior User';
    return 'Basic User';
  };

  const handlePermissionChange = (permission, checked) => {
    if (checked) {
      setNewRole(prev => ({
        ...prev,
        permissions: [...prev.permissions, permission]
      }));
    } else {
      setNewRole(prev => ({
        ...prev,
        permissions: prev.permissions.filter(p => p !== permission)
      }));
    }
  };

  const formatPermissionName = (permission) => {
    const permissionString = typeof permission === 'string' ? permission : permission?.name || permission;
    
    if (!permissionString || typeof permissionString !== 'string') {
      return 'Unknown Permission';
    }
    
    return permissionString.split(':').map(part => 
      part.charAt(0).toUpperCase() + part.slice(1).replace(/_/g, ' ')
    ).join(' - ');
  };

  // Filter roles based on search and filters
  const filteredRoles = roles.filter(role => {
    const matchesSearch = role.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         role.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         role.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesLevel = filterLevel === 'all' || 
                        (filterLevel === 'admin' && role.level >= 70) ||
                        (filterLevel === 'manager' && role.level >= 40 && role.level < 70) ||
                        (filterLevel === 'user' && role.level < 40);
    
    const matchesType = filterType === 'all' ||
                       (filterType === 'system' && role.isSystemRole) ||
                       (filterType === 'custom' && !role.isSystemRole);
    
    return matchesSearch && matchesLevel && matchesType;
  });

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  // Table columns definition
  const columns = [
    {
      id: 'role',
      header: 'Role',
      sortable: true,
      render: (_, role) => (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
            <ShieldCheckIconSolid className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-medium text-gray-900 dark:text-white">
              {role.displayName}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-300">
              {role.name}
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'description',
      header: 'Description',
      sortable: false,
      render: (_, role) => (
        <div className="max-w-xs">
          <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
            {role.description}
          </p>
        </div>
      )
    },
    {
      id: 'level',
      header: 'Level',
      sortable: true,
      render: (_, role) => (
        <div className="space-y-1">
          <StatusBadge 
            status={`Level ${role.level}`}
            color={getRoleLevelColor(role.level)}
          />
          <div className="text-xs text-gray-500 dark:text-gray-300">
            {getRoleLevelText(role.level)}
          </div>
        </div>
      )
    },
    {
      id: 'type',
      header: 'Type',
      sortable: true,
      render: (_, role) => (
        <StatusBadge 
          status={role.isSystemRole ? 'System' : 'Custom'}
          color={role.isSystemRole ? 'secondary' : 'success'}
        />
      )
    },
    {
      id: 'users',
      header: 'Users',
      sortable: true,
      render: (_, role) => (
        <div className="flex items-center space-x-1">
          <UsersIcon className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {role.userCount || 0}
          </span>
        </div>
      )
    },
    {
      id: 'permissions',
      header: 'Permissions',
      sortable: true,
      render: (_, role) => (
        <div className="flex items-center space-x-1">
          <KeyIcon className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {role.permissions?.length || 0}
          </span>
        </div>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      sortable: false,
      render: (_, role) => (
        <ActionMenu
          items={[
            {
              label: 'View Details',
              icon: <EyeIcon />,
              onClick: () => {
                setSelectedRole(role);
                setShowPermissionsPanel(true);
              }
            },
            {
              label: 'Edit Role',
              icon: <PencilIcon />,
              onClick: () => handleEditClick(role),
              disabled: role.isSystemRole
            },
            {
              label: 'Manage Users',
              icon: <UsersIcon />,
              onClick: () => {
                showNotification?.('User management coming soon', 'info');
              }
            },
            { type: 'divider' },
            {
              label: 'Delete',
              icon: <TrashIcon />,
              onClick: () => handleDeleteRole(role._id),
              className: 'text-red-600 hover:text-red-700',
              disabled: role.isSystemRole
            }
          ]}
        />
      )
    }
  ];

  const handleRowClick = (role) => {
    setSelectedRole(role);
    setShowPermissionsPanel(true);
  };

  return (
    <div className="h-full bg-gray-50 dark:bg-[#111111] flex flex-col">
      {/* Header */}
      <div className="bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800 sticky top-0 z-10 px-6 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheckIconSolid className="w-4 h-4 text-orange-600 dark:text-orange-400" />
            <h1 className="text-sm font-semibold text-gray-900 dark:text-white">Role Management</h1>
            <span className="text-xs text-gray-400 dark:text-gray-500">{filteredRoles.length} {filteredRoles.length === 1 ? 'role' : 'roles'}</span>
          </div>
          <div className="flex items-center gap-2">
            {roles.length === 0 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={initializeRoles}
                icon={<ShieldCheckIcon />}
              >
                Initialize Default Roles
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowCreatePanel(true)}
              icon={<PlusIcon />}
            >
              Create Role
            </Button>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <SearchInput
              placeholder="Search roles by name, description, or permissions..."
              onSearch={handleSearch}
              className="w-full"
            />
          </div>
          
          <div className="flex space-x-3">
            <select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">All Levels</option>
              <option value="admin">Admin (70+)</option>
              <option value="manager">Manager (40-69)</option>
              <option value="user">User (0-39)</option>
            </select>
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">All Types</option>
              <option value="system">System Roles</option>
              <option value="custom">Custom Roles</option>
            </select>
            
            <Button
              variant="secondary"
              onClick={() => fetchRoles()}
              loading={loading}
              icon={<ArrowPathIcon />}
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Content - Scrollable Area */}
        <div className="flex-1 overflow-y-auto pb-6">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-2 border-orange-500 border-t-transparent mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-300">Loading roles...</p>
              </div>
            </div>
          ) : filteredRoles.length === 0 ? (
            <div className="text-center py-20">
              <div className="max-w-md mx-auto">
                <ShieldCheckIconSolid className="w-20 h-20 text-gray-300 dark:text-gray-600 mx-auto mb-6" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {roles.length === 0 ? 'No roles found' : 'No matching roles'}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  {roles.length === 0 
                    ? 'Get started by creating your first custom role or initializing default roles.'
                    : 'Try adjusting your search criteria or filters.'
                  }
                </p>
                {roles.length === 0 && (
                  <div className="flex justify-center gap-3">
                    <Button
                      variant="secondary"
                      onClick={initializeRoles}
                      icon={<ShieldCheckIcon />}
                    >
                      Initialize Defaults
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => setShowCreatePanel(true)}
                      icon={<PlusIcon />}
                    >
                      Create Role
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table
                columns={columns}
                data={filteredRoles}
                loading={loading}
                onRowClick={handleRowClick}
                emptyState={{
                  icon: <ShieldCheckIconSolid className="w-12 h-12 text-gray-400" />,
                  title: roles.length === 0 ? "No roles found" : "No roles match your search",
                  description: roles.length === 0 
                    ? "Get started by creating your first custom role or initializing default roles."
                    : "Try adjusting your search criteria or filters.",
                  action: roles.length === 0 ? (
                    <div className="flex justify-center gap-3">
                      <Button
                        variant="secondary"
                        onClick={initializeRoles}
                        icon={<ShieldCheckIcon />}
                      >
                        Initialize Defaults
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => setShowCreatePanel(true)}
                        icon={<PlusIcon />}
                      >
                        Create Role
                      </Button>
                    </div>
                  ) : null
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Create Role Side Panel */}
      <SidePanel
        isOpen={showCreatePanel}
        onClose={() => {
          setShowCreatePanel(false);
          resetForm();
        }}
        title={
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
              <PlusIcon className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="font-semibold text-sm text-gray-900 dark:text-white">Create New Role</div>
              <div className="text-xs text-gray-600 dark:text-gray-300">Define permissions and access levels</div>
            </div>
          </div>
        }
        size="3xl"
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto p-4">
            <RoleForm
              isEdit={false}
              newRole={newRole}
              setNewRole={setNewRole}
              formErrors={formErrors}
              setFormErrors={setFormErrors}
              availablePermissions={availablePermissions}
              handlePermissionChange={handlePermissionChange}
              formatPermissionName={formatPermissionName}
            />
          </div>

          <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#1a1a1a] px-4 py-3">
            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowCreatePanel(false);
                  resetForm();
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateRole}
                loading={isSubmitting}
                disabled={isSubmitting || !newRole.name.trim() || !newRole.displayName.trim() || !newRole.description.trim()}
                icon={<CheckCircleIcon />}
              >
                {isSubmitting ? 'Creating...' : 'Create Role'}
              </Button>
            </div>
          </div>
        </div>
      </SidePanel>

      {/* Edit Role Side Panel */}
      <SidePanel
        isOpen={showEditPanel}
        onClose={() => {
          setShowEditPanel(false);
          resetForm();
        }}
        title={
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <PencilIcon className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="font-semibold text-sm text-gray-900 dark:text-white">
                Edit Role: {selectedRole?.displayName}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-300">Modify permissions and settings</div>
            </div>
          </div>
        }
        size="3xl"
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto p-4">
            <RoleForm
              isEdit={true}
              newRole={newRole}
              setNewRole={setNewRole}
              formErrors={formErrors}
              setFormErrors={setFormErrors}
              availablePermissions={availablePermissions}
              handlePermissionChange={handlePermissionChange}
              formatPermissionName={formatPermissionName}
            />
          </div>

          <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#1a1a1a] px-4 py-3">
            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowEditPanel(false);
                  resetForm();
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleEditRole}
                loading={isSubmitting}
                disabled={isSubmitting}
                icon={<CheckCircleIcon />}
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      </SidePanel>

      {/* Role Details Panel */}
      <SidePanel
        isOpen={showPermissionsPanel}
        onClose={() => {
          setShowPermissionsPanel(false);
          setSelectedRole(null);
        }}
        title={
          selectedRole && (
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                <EyeIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-semibold text-gray-900 dark:text-white">
                  {selectedRole.displayName}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">View role details and permissions</div>
              </div>
            </div>
          )
        }
        size="2xl"
      >
        {selectedRole && (
          <div className="p-6 space-y-6">
            {/* Role Overview */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-[#1a1a1a] dark:to-[#222222] rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Role Overview</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Internal Name</label>
                  <p className="font-mono text-sm text-gray-900 dark:text-white">{selectedRole.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Permission Level</label>
                  <div className="mt-1">
                    <StatusBadge 
                      status={`Level ${selectedRole.level}`}
                      color={getRoleLevelColor(selectedRole.level)}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Type</label>
                  <div className="mt-1">
                    <StatusBadge 
                      status={selectedRole.isSystemRole ? 'System' : 'Custom'}
                      color={selectedRole.isSystemRole ? 'secondary' : 'success'}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Active Users</label>
                  <p className="font-semibold text-gray-900 dark:text-white">{selectedRole.userCount || 0}</p>
                </div>
              </div>
              <div className="mt-4">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Description</label>
                <p className="text-sm text-gray-900 dark:text-white mt-1">{selectedRole.description}</p>
              </div>
            </div>

            {/* Permissions */}
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
                Permissions ({selectedRole.permissions?.length || 0})
              </h3>
              {selectedRole.permissions?.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {selectedRole.permissions.map((permission, idx) => (
                    <div key={idx} className="flex items-center space-x-3 p-3 bg-white dark:bg-[#222222] border border-gray-200 dark:border-gray-800 rounded-lg">
                      <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                        <CheckCircleIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {formatPermissionName(permission)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-300">
                          {typeof permission === 'string' ? permission : permission?.name || 'Unknown'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <LockClosedIconSolid className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-300">No permissions assigned to this role</p>
                </div>
              )}
            </div>
          </div>
        )}
      </SidePanel>
    </div>
  );
};

export default ModernRoleManagement;